import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Loader2, Sparkles, Check, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RefreshSuggestionDialog = ({ blockId, blockTitle, onOpenChange, onApplied }) => {
  const [sug, setSug] = useState(null);
  const [failed, setFailed] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!blockId) return;
    setSug(null);
    setFailed(false);
    axios.post(`${API_URL}/api/rfp/boilerplate/${blockId}/refresh-suggestion`, {}, { withCredentials: true })
      .then(res => setSug(res.data))
      .catch(err => { toast.error(err?.response?.data?.detail || 'AI refresh failed'); setFailed(true); });
  }, [blockId]);

  const apply = async () => {
    setApplying(true);
    try {
      await axios.put(`${API_URL}/api/rfp/boilerplate/${blockId}`,
        { title: sug.title, body: sug.body }, { withCredentials: true });
      toast.success('Refreshed wording applied — the old version is in History and keywords are regenerating');
      onOpenChange(false);
      onApplied();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply the refresh');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={!!blockId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="rfp-refresh-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#4ABFCF]" />AI Refresh — {blockTitle}
          </DialogTitle>
          <DialogDescription>
            A modernised rewrite keeping every factual claim. Edit freely — applying archives the current wording to History.
          </DialogDescription>
        </DialogHeader>
        {failed ? (
          <p className="text-sm text-rose-600 py-4 text-center">No suggestion available — try again in a moment.</p>
        ) : sug === null ? (
          <div className="flex flex-col items-center gap-2 py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
            <p className="text-xs text-slate-500">AI is rewriting the capability…</p>
          </div>
        ) : (
          <div className="space-y-2">
            <Input value={sug.title} onChange={e => setSug(s => ({ ...s, title: e.target.value }))}
              className="h-8 text-xs" data-testid="rfp-refresh-title" />
            <Textarea value={sug.body} onChange={e => setSug(s => ({ ...s, body: e.target.value }))}
              rows={10} className="text-xs" data-testid="rfp-refresh-body" />
          </div>
        )}
        <DialogFooter>
          <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="rfp-refresh-cancel">
            <X className="w-3 h-3 mr-1" />Cancel
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={apply} disabled={!sug || applying} data-testid="rfp-refresh-apply">
            {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
            Apply refresh
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
